import { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";

const CallPermission = ({
  showCallPermission,
  setShowCallPermission,
  handleCall,
  onDecline,
}) => {
  const [isVideo, setIsVideo] = useState(false);
  const [isAudio, setIsAudio] = useState(false);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  // Update video element when stream changes
  useEffect(() => {
    if (videoRef.current && stream && isVideo) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, isVideo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  // Auto-start camera and microphone when modal opens
  useEffect(() => {
    if (showCallPermission) {
      const initializeDevices = async () => {
        try {
          // Request both video and audio by default
          await requestMediaAccess(true, true);
          setIsVideo(true);
          setIsAudio(true);
        } catch (err) {
          // If both fail, try video only
          try {
            await requestMediaAccess(true, false);
            setIsVideo(true);
            setIsAudio(false);
          } catch (videoErr) {
            // If video fails, try audio only
            try {
              await requestMediaAccess(false, true);
              setIsVideo(false);
              setIsAudio(true);
            } catch (audioErr) {
              // Both devices failed
              setIsVideo(false);
              setIsAudio(false);
            }
          }
        }
      };

      initializeDevices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCallPermission]);

  // Handle video toggle
  const handleVideoToggle = async () => {
    try {
      if (!isVideo) {
        // Enable video
        setIsVideo(true);

        // Create new stream with current audio state + video
        await requestMediaAccess(true, isAudio);
      } else {
        // Disable video
        setIsVideo(false);

        if (stream) {
          // Stop video tracks
          const videoTracks = stream.getVideoTracks();
          videoTracks.forEach((track) => track.stop());

          // If audio is still needed, create new stream with only audio
          if (isAudio) {
            await requestMediaAccess(false, true);
          } else {
            // No devices needed, clear stream
            stream.getTracks().forEach((track) => track.stop());
            setStream(null);
          }
        }
      }
    } catch (err) {
      setIsVideo(false); // Reset state on error
    }
  };

  // Handle audio toggle
  const handleAudioToggle = async () => {
    try {
      if (!isAudio) {
        // Enable audio
        setIsAudio(true);

        if (stream && isVideo) {
          // If we have video stream, add audio track to it
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          const audioTrack = audioStream.getAudioTracks()[0];

          // Stop the temporary audio stream
          audioStream.getTracks().forEach((track) => track.stop());

          // Add audio track to existing video stream
          stream.addTrack(audioTrack);
          setStream(stream);
        } else {
          // No video stream, create new stream with audio
          await requestMediaAccess(isVideo, true);
        }
      } else {
        // Disable audio
        setIsAudio(false);

        if (stream) {
          // Stop audio tracks
          const audioTracks = stream.getAudioTracks();
          audioTracks.forEach((track) => {
            track.stop();
            stream.removeTrack(track);
          });

          // If no tracks left, clear stream
          if (stream.getTracks().length === 0) {
            setStream(null);
          } else {
            setStream(stream);
          }
        }
      }
    } catch (err) {
      setIsAudio(false); // Reset state on error
    }
  };

  // Handle decline call
  const handleDeclineCall = () => {
    // Stop all tracks
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setIsVideo(false);
    setIsAudio(false);
    setShowCallPermission(false);

    // Call the onDecline function if provided (for incoming calls)
    if (onDecline) {
      onDecline();
    }
  };

  // Request media permissions and start stream
  const requestMediaAccess = async (video = false, audio = false) => {
    try {
      setError(null);

      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      // Request new stream
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: video,
        audio: audio,
      });

      setStream(mediaStream);

      // Set video element source if video is enabled
      if (video && videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }

      return mediaStream;
    } catch (err) {
      setError(err.message);

      // Reset states on error
      if (!video) setIsVideo(false);
      if (!audio) setIsAudio(false);

      throw err;
    }
  };

  return (
    <Modal
      show={showCallPermission}
      onHide={handleDeclineCall}
      size="md"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <div
        id="modal-invite"
        tabIndex="-1"
        aria-labelledby="modal-invite"
        aria-hidden="true"
      >
        <div
          className="modal-content"
          style={{
            border:
              localStorage.getItem("theme") === "dark"
                ? "1px solid #2a2a2a"
                : "1px solid #e0e0e0",
          }}
        >
          <div className="modal-body py-8 d-grid justify-content-center gap-5">
            <div
              style={{
                width: 300,
                aspectRatio: "3/2",
                backgroundColor: "gray",
                borderRadius: 20,
                overflow: "hidden",
                position: "relative",
              }}
            >
              {isVideo && stream ? (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color:
                      localStorage.getItem("theme") === "dark"
                        ? "white"
                        : "black",
                    backgroundColor:
                      localStorage.getItem("theme") === "dark"
                        ? "#2a2a2a"
                        : "#e0e0e0",
                  }}
                >
                  <i className="fal fa-video-slash" style={{ fontSize: 40 }} />
                </div>
              )}
            </div>

            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="fal fa-exclamation-triangle" /> {error}
              </div>
            )}

            <p
              style={{
                fontSize: 16,
                fontWeight: 600,
                textAlign: "center",
                marginBottom: 0,
              }}
            >
              Ready to join?
            </p>
            <div
              style={{
                padding: 10,
                backgroundColor: "#2E2E2E66",
                maxWidth: "max-content",
                margin: "auto",
                borderRadius: 100,
                display: "flex",
                gap: 10,
              }}
            >
              <button
                className="call-button active-call-button"
                onClick={handleVideoToggle}
                title={isVideo ? "Turn off camera" : "Turn on camera"}
              >
                {isVideo ? (
                  <i className="fal fa-video" />
                ) : (
                  <i className="fal fa-video-slash text-danger" />
                )}
              </button>
              <button
                className="call-button active-call-button"
                onClick={handleAudioToggle}
                title={isAudio ? "Mute microphone" : "Unmute microphone"}
              >
                {isAudio ? (
                  <i className="fal fa-microphone" />
                ) : (
                  <i className="fal fa-microphone-slash text-danger" />
                )}
              </button>
              <button
                className="call-button btn-primary"
                onClick={() => {
                  // Save media device permissions to sessionStorage
                  const mediaDevicesOptions = {
                    micEnabled: isAudio,
                    webcamEnabled: isVideo,
                  };
                  sessionStorage.setItem(
                    "mediaDevicesOptions",
                    JSON.stringify(mediaDevicesOptions)
                  );

                  // Call the handleCall function with the correct format
                  handleCall("s-t-r-e-a-m", mediaDevicesOptions);
                }}
                title="Join call"
                style={{ fontSize: 12, fontWeight: 600 }}
              >
                <i className="fal fa-phone" />
              </button>
              <button
                className="call-button btn-danger"
                onClick={handleDeclineCall}
                title="Decline call"
                style={{ fontSize: 12, fontWeight: 600 }}
              >
                <i className="fal fa-phone" style={{ rotate: "225deg" }} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CallPermission;
